import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Button, Container } from 'react-bootstrap';
import { Formik } from 'formik';

import history from 'browserHistory';
import AdminTop from 'modules/admin/components/AdminTop';
import BranchDataCard from 'modules/admin/components/cards/BranchDataCard';
import FormikPatchTouched from 'modules/shared/Forms/FormikPatchTouched';
import OrderingCard from 'modules/admin/components/cards/OrderingCard';
import LocationsCard from 'modules/admin/components/cards/LocationsCard';
import SecurityCard from 'modules/admin/components/cards/SecurityCard';
import Loader from 'modules/kiosk/components/Loader';
import ErrorMsg from 'modules/shared/ErrorMsg';
import { useModal } from 'modules/shared/hooks';
import { clearBranchState, postNewBranchRequest } from 'modules/admin/containers/BranchEditing/actions';
import SettingsDocumentsCard from 'modules/admin/components/cards/SettingsDocumentsCard';
import SettingsSMSCard from 'modules/admin/components/cards/SettingsSMSCard';
import ModalWrapper from 'modules/shared/ModalWrapper';
import { deleteTab } from 'modules/common/Tabs/actions';
import { getBranchError, getBranchRequest, putBranchRequest, deleteBranchRequest } from './actions';
import { branchSelector } from './selectors';
import { usePrepareBranch } from './hooks/usePrepareBranch';
import branchEditingSchema from './schema';
import styles from './BranchEditing.module.scss';
import { setSMSFlag } from '../../../common/CommonTrip/actions';
import TvPanel from '../TvPanel';

type Params = {
  id: string;
};

const BranchEditing: React.FC = () => {
  const dispatch = useDispatch();
  const { id = '' } = useParams<Params>();

  const { loading, error, branch } = useSelector(branchSelector);
  const [customTouched, setCustomTouched] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [errorTouched, setErrorTouched] = useState(false);

  const { isShow, openModal, closeModal } = useModal();
  const { isShow: isShowSave, openModal: openModalSave, closeModal: closeModalSave } = useModal();
  const { isShow: isShowTouched, openModal: openModalTouched, closeModal: closeModalTouched } = useModal();

  // @ts-ignore
  const branchId = branch?.id;
  const initValues = usePrepareBranch(+id, branch);
  const isCreate = id === 'create';
  const tab = isCreate ? 'branch_create' : `branch_${id}`;

  const onClose = () => {
    dispatch(deleteTab(tab));
    dispatch(clearBranchState());

    return history.push('/globaladmin');
  };

  const closeTabHandler = () => {
    const timer = setTimeout(() => {
      dispatch(deleteTab(tab));
      dispatch(clearBranchState());
      closeModalSave();

      return history.push('/globaladmin');
    }, 3000);

    clearTimeout(timer);
    dispatch(deleteTab(tab));
    dispatch(clearBranchState());
    closeModalSave();

    return history.push('/globaladmin');
  };

  const onDeleteBranch = () => {
    if (!isCreate) dispatch(deleteBranchRequest(branchId, true));

    setTimeout(() => onClose(), 300);
  };

  useEffect(() => {
    if (id && !branchId && !isCreate) {
      setCustomTouched(false);
      dispatch(getBranchRequest(id));
    }
  }, [id]);

  useEffect(() => {
    if (error) {
      closeModalSave();
      return openModal();
    }
    closeModal();
  }, [error]);

  useEffect(() => {
    if (errorTouched) {
      return openModalTouched();
    }
    closeModalTouched();
  }, [errorTouched]);

  useEffect(() => {
    if (!error && submit) {
      openModalSave();
    }
    setSubmit(false);
  }, [submit]);

  return (
    <Container className="my-4">
      {loading && <Loader />}

      {!loading && id ? (
        <Formik
          initialValues={initValues}
          validationSchema={branchEditingSchema}
          validateOnMount
          validateOnChange
          enableReinitialize
          onSubmit={async (values) => {
            const body = {
              ...values,
              screenRenderSettings: {
                ...values.screenRenderSettings,
                logoStartTime: moment(values.screenRenderSettings?.logoStartTime).format('YYYY-MM-DDTHH:mm:00'),
                logoEndTime: moment(values.screenRenderSettings?.logoEndTime).format('YYYY-MM-DDTHH:mm:00'),
              },
            };

            if (body.ordering && Array.isArray(body.ordering.peripheralRoles)) {
              // @ts-ignore
              body.ordering.peripheralRoles = body.ordering.peripheralRoles.map((role) => {
                if (role.peripheralUrl) {
                  return {
                    ...role,
                    peripheralUrl: btoa(role.peripheralUrl),
                  };
                }
                return role;
              });
            }

            if (body.securityGates && Array.isArray(body.securityGates)) {
              // @ts-ignore
              body.securityGates = body.securityGates.map((gate) => {
                if (gate.peripheralRoles && Array.isArray(gate.peripheralRoles)) {
                  // @ts-ignore
                  gate.peripheralRoles = gate.peripheralRoles.map((role) => {
                    if (role.peripheralUrl) {
                      return {
                        ...role,
                        peripheralUrl: btoa(role.peripheralUrl),
                      };
                    }
                    return role;
                  });
                }
                return gate;
              });
            }

            if (isCreate) {
              dispatch(postNewBranchRequest(body));
            } else {
              dispatch(putBranchRequest(body));
              dispatch(setSMSFlag(body));
            }

            setTimeout(() => setSubmit(true), 300);
          }}
        >
          {({ values, isValid, handleSubmit, handleChange }) => {
            if (isValid && customTouched) setCustomTouched(false);
            const onSubmitClick = () => {
              setCustomTouched(true);

              if (!isValid) {
                setErrorTouched(true);
                window.scrollTo(0, 0);

                setTimeout(() => setErrorTouched(false), 3000);
              }
              handleSubmit(values);
            };
            return (
              <form onSubmit={handleSubmit} className={styles.branchCont}>
                <FormikPatchTouched />
                <AdminTop
                  title={`${isCreate ? 'Створення' : 'Редагування'} філії`}
                  branchId={branchId}
                  onSave={onSubmitClick}
                  onClose={onClose}
                  onDelete={onDeleteBranch}
                />
                <BranchDataCard values={values} onChange={handleChange} />
                <LocationsCard values={values} onChange={handleChange} />
                <TvPanel values={values} onChange={handleChange} />
                <SettingsSMSCard onChange={handleChange} values={values} />
                <SettingsDocumentsCard onChange={handleChange} />
                <OrderingCard
                  onChange={handleChange}
                  peripherals={values.peripheralHardwares}
                  ordering={values.ordering.peripheralRoles}
                />
                {!!values.securityGates[0].name && values.securityGates[0].name.length
                  ? values.securityGates.map((post: any, index: number) =>
                      post.name.length > 1 && !post.isDeleted ? (
                        <SecurityCard
                          key={index}
                          idx={index}
                          value={post}
                          onChange={handleChange}
                          peripherals={values.peripheralHardwares}
                        />
                      ) : null,
                    )
                  : null}
                {/* <div className="d-flex justify-content-end mt-4 mb-5"> */}
                {/*  <Button */}
                {/*    disabled={customTouched} */}
                {/*    onClick={onSubmitClick} */}
                {/*    className="px-3 text-uppercase mr-5" */}
                {/*    variant="danger" */}
                {/*  > */}
                {/*    <h5 className="my-1 text-white mx-4">Зберегти зміни</h5> */}
                {/*  </Button> */}
                {/* </div> */}
              </form>
            );
          }}
        </Formik>
      ) : null}

      {!loading && !error && (
        <ModalWrapper show={isShowSave} onHide={closeTabHandler}>
          <div>
            <h6 className="text-center">Зміни збережено</h6>
            <div className="d-flex justify-content-center mt-3">
              <Button className="px-5" variant="outline-secondary" onClick={closeTabHandler}>
                Ok
              </Button>
            </div>
          </div>
        </ModalWrapper>
      )}

      <ErrorMsg
        isShow={isShow}
        closeHandler={() => {
          closeModal();
          dispatch(getBranchError(null));
        }}
        // @ts-ignore
        error={error?.message || 'Виникла помилка'}
      />
      <ErrorMsg
        isShow={isShowTouched}
        closeHandler={() => {
          closeModalTouched();
        }}
        error="Заповніть всі обов'язкові поля"
      />
    </Container>
  );
};

export default BranchEditing;
